/**
 * EDM vertical slice — document detail header plus the revision list.
 *
 * Deliberately nothing else: no register, no viewer, no tabs. The point of this
 * screen existing before P4 is to settle whether three labelled "current"
 * revisions in one header reads as rigorous or as confusing, before six more
 * screens are built on the assumption.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Clock, FileText, Loader2, Plus, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import EdmCurrentNotions from '@/components/edm/EdmCurrentNotions';
import EdmRevisionList from '@/components/edm/EdmRevisionList';
import EdmOpenRevisionDialog from '@/components/edm/EdmOpenRevisionDialog';

const Meta = ({ t, label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: t.muted }}>
      {label}
    </div>
    <div className="text-sm" style={{ color: t.body }}>{value || '—'}</div>
  </div>
);

export default function EdmDocumentDetail() {
  const t = useNfaTheme();
  const { documentId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [meta, setMeta] = useState(null);
  const [showRevision, setShowRevision] = useState(false);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    api.get(`/edm/documents/${documentId}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.status === 404
        ? 'This document does not exist, or it is outside your access.'
        : (e?.response?.data?.detail || 'Could not load this document')));
  }, [documentId]);
  useEffect(load, [load]);

  useEffect(() => {
    const pid = d?.document?.project_id;
    if (!pid || meta) return;
    api.get(`/edm/meta?project_id=${pid}`).then((r) => setMeta(r.data)).catch(() => {});
  }, [d, meta]);

  const upload = useCallback(async (revisionId, file) => {
    if (!file) return;
    setUploading(revisionId);
    const form = new FormData();
    form.append('file', file);
    form.append('role', 'native');
    try {
      await api.post(`/edm/revisions/${revisionId}/versions`, form);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  }, [load]);

  if (err) {
    return (
      <DashboardLayout>
        <div className="p-8 text-sm" data-testid="edm-document-error"
          style={{ color: t.red }}>{err}</div>
      </DashboardLayout>
    );
  }
  if (!d) {
    return (
      <DashboardLayout>
        <div className="flex items-center gap-2 p-8 text-sm" data-testid="edm-document-loading"
          style={{ color: t.muted }}>
          <Loader2 size={14} className="animate-spin" /> Loading document…
        </div>
      </DashboardLayout>
    );
  }

  const doc = d.document || {};
  const scheme = d.revisionScheme || {};
  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="edm-document-detail"
        style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-1.5 text-[10.5px]" style={{ color: t.muted }}>
          <span>Project Controls</span>
          <span>›</span>
          <a href="/project-controls/dms" data-testid="edm-crumb-register"
            style={{ color: t.body }}>Document Management</a>
          <span>›</span>
          <span className="px-1.5 py-0.5 rounded font-mono"
            style={{ background: t.tint(t.primary), color: t.primary }}>
            {doc.document_no}</span>
        </div>

        {/* ── header ─────────────────────────────────────────────── */}
        <header className="rounded-lg p-4"
          style={{ background: t.surface, border: `1px solid ${t.border}` }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <a href="/project-controls/dms" data-testid="edm-back-to-register"
                className="inline-flex items-center gap-1 text-[10.5px] mb-2"
                style={{ color: t.muted }}>
                <ArrowLeft size={11} /> Document register
              </a>
              <div className="flex items-center gap-2">
                <FileText size={14} style={{ color: t.primary }} />
                <span className="font-mono text-sm tracking-tight"
                  style={{ color: t.primary }} data-testid="document-no">
                  {doc.document_no}
                </span>
              </div>
              <h1 className="mt-1 text-[22px] font-bold leading-tight"
                style={{ color: t.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}
                data-testid="document-title">
                {doc.title}
              </h1>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-1
                               rounded-full"
                style={{
                  background: t.tint(t.primary, '1A'), color: t.primary,
                  border: `1px solid ${t.tint(t.primary, '3A')}`,
                }}
                data-testid="revision-scheme-badge">
                {scheme.label || 'revision scheme'}
              </span>
              <span className="font-mono text-[10px]" style={{ color: t.muted }}
                data-testid="next-revision-hint">
                next revision: {d.nextRevision?.revision_code || '—'}
              </span>
              <button onClick={() => setShowRevision(true)} data-testid="edm-open-revision-btn"
                className="mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold"
                style={{ background: t.primary, color: t.page }}>
                <Plus size={13} /> Open revision
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <Meta t={t} label="Discipline" value={doc.discipline} />
            <Meta t={t} label="Type" value={doc.doc_type} />
            <Meta t={t} label="Area / system" value={[doc.area, doc.system].filter(Boolean).join(' · ')} />
            <Meta t={t} label="Originator" value={doc.originator} />
            <Meta t={t} label="Contract" value={doc.contract_no} />
          </div>
        </header>

        {/* ── overdue review nudges, where people already look ───── */}
        {d.reviewNudges?.items?.length > 0 && (
          <section className="mt-5 rounded-xl p-4" data-testid="edm-review-nudges"
            style={{
              background: d.reviewNudges.overdueCount ? t.tint(t.red, '14') : t.surfaceAlt,
              border: `1px solid ${d.reviewNudges.overdueCount
                ? t.tint(t.red, '44') : t.border}`,
            }}>
            <div className="flex items-start gap-2">
              <Clock size={14} className="mt-0.5 shrink-0"
                style={{ color: d.reviewNudges.overdueCount ? t.red : t.muted }} />
              <div className="min-w-0">
                <p className="text-[12px] font-semibold" data-testid="edm-nudge-headline"
                  style={{ color: d.reviewNudges.overdueCount ? t.red : t.body }}>
                  {d.reviewNudges.headline
                    || 'Review in progress and not yet past its due date.'}
                </p>
                <ul className="mt-2 space-y-1">
                  {d.reviewNudges.items.map((n) => (
                    <li key={n.cycle_id} className="text-[11px]"
                      data-testid={`edm-nudge-${n.revision_code}-${n.cycle_no}`}
                      style={{ color: n.overdue ? t.ink : t.muted }}>
                      <span className="font-mono text-[10px] mr-1.5"
                        style={{ color: n.overdue ? t.red : t.muted }}>
                        {n.overdue ? `+${n.daysOverdue}d` : `${n.daysRemaining}d left`}
                      </span>
                      {n.statement}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* ── the three notions ──────────────────────────────────── */}
        <div className="mt-8">
          <EdmCurrentNotions t={t} data={d} />
        </div>

        {/* ── revision history ───────────────────────────────────── */}
        <EdmRevisionList t={t} revisions={d.revisions || []} onUpload={upload}
          uploading={uploading} />

        {showRevision && (
          <EdmOpenRevisionDialog
            t={t} documentId={documentId}
            nextRevision={d.nextRevision?.revision_code}
            statusCodes={meta?.statusCodes || []}
            onClose={() => setShowRevision(false)}
            onOpened={() => { setShowRevision(false); load(); }} />
        )}

        <footer className="mt-8 flex items-start gap-2 rounded-lg px-3 py-2"
          data-testid="edm-derivation-footer"
          style={{ background: t.surfaceAlt, border: `1px solid ${t.border}` }}>
          <ShieldCheck size={14} className="mt-0.5 shrink-0" style={{ color: t.green }} />
          <span className="text-[11px] leading-snug" style={{ color: t.muted }}>
            {d.definitions?.derivation}
          </span>
        </footer>
      </div>
    </DashboardLayout>
  );
}
